/**
 * Created by rathompson on 8/16/22
 * File: DeleteTSGModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useState, useCallback, useContext, useMemo, useEffect } from 'react'
import { Bold, Button, LoadingIcon, Modal, ModalBody, ModalFooter, ModalHeader } from '@panwds/react-ui'
import { Body } from '@panwds/react-table'
import StringTemplate from '../../../../common/utils/StringTemplate'
import {
  BUTTON_CONFIRM_YES_DELETE_TENANT,
  PARAGRAPH_DELETE_CONTINUE_PROMPT,
  PARAGRAPH_DELETE_TENANT,
  PARAGRAPH_DELETE_TENANT_CANNOT_UNDONE,
  TITLE_DELETE_TENANT,
  LABEL_CANNOT_DELETE_TSG_PRODUCTS,
  LABEL_CANNOT_DELETE_TSG_CLAIMS,
  LABEL_CANNOT_DELETE_TSG_CHILDREN, LABEL_CANNOT_DELETE_TSG_ACCESS,
} from '../../../constants/LabelConstants'
import TenancyService from '../../../services/TenancyService'
import { GetClaimedInfo, LoadTreeData } from '../../../../common/actions/GlobalActions'
import { BUTTON_CANCEL } from '../../../../common/constants/LabelConstants'
import GlobalContext from '../../../../common/context/GlobalContext'
import { ROOT_NODE_ID } from '../../../constants/TenancyConstants'
import { ARRAY } from '../../../../common/utils/Utils'
import { dispatch as globalDispatch } from '@sparky/framework'
import _ from 'lodash'
import { getVarValue } from '../../../../common/utils/Sparky'
import { FF_ENHANCED_TSG_DELETE } from '../../../../common/utils/FeatureFlags'
import IAMAppName from '../../../../iam/utils/IAMAppName'
import AppContext from '../../../../common/context/AppContext'
import useReadOnly from '../../../../common/hooks/useReadOnly'
import { TENANCY_PERMISSION_SET, TSG_NONE } from '../../../../common/constants/GlobalConstants'

const enhancedTSGDelete = getVarValue(FF_ENHANCED_TSG_DELETE, false)

const getNextTSG_ID = (tsg_id, parent_id, tenants) => {
  const availableTenants = tenants.filter(tsg => tsg.id !== ROOT_NODE_ID && tsg.id !== tsg_id)
  if (!ARRAY.hasArray(availableTenants)) {
    return TSG_NONE.id
  }
  const parent_tenant = availableTenants.find(tenant => tenant.id === parent_id)
  if (parent_tenant) {
    return parent_id
  }
  return availableTenants[0].id
}

const DeleteTSGModal = ({
  closeModal,
  isOpen,
  tenant,
  instances = []
}) => {

  const { getAppName } = useContext(AppContext)
  const { treeReducer, dispatch } = useContext(GlobalContext)
  const { globalTsgId, sgKeyBy, flattenTree } = treeReducer

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { selectedTreeNode = {} } = treeReducer
  const readOnly = useReadOnly(selectedTreeNode?.id, TENANCY_PERMISSION_SET)

  useEffect(() => {
    const modalMask = document.getElementsByClassName('tw-bg-gray-900 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-75')
    if (modalMask && modalMask.length) {
      modalMask[0].style.zIndex = 1052
    }
  }, [isOpen])


  const [parentDisplayName, currentNode] = useMemo(() => {
    const parent_id = tenant?.parent_id
    const id = tenant?.id
    const parentDisplayName = sgKeyBy[id]?.display_name
    const currentNode = sgKeyBy[parent_id]?.display_name
    return [parentDisplayName, currentNode]

  }, [sgKeyBy, tenant?.id, tenant?.parent_id])

  const onDeleteTenant = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      await TenancyService.listTenant.delete(tenant?.id)
      if (tenant?.id === globalTsgId) {
        const next_tsg_id = getNextTSG_ID(tenant?.id, tenant?.parent_id, flattenTree)
        await globalDispatch.switchTsg(next_tsg_id, { forceReload: next_tsg_id === TSG_NONE.id })
      }
      LoadTreeData(dispatch)
      closeModal()
    }
    catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }, [tenant?.id, tenant?.parent_id, globalTsgId, dispatch, closeModal, flattenTree])

  const deleteDisabled = useMemo(() => {
    if (!enhancedTSGDelete) {
      return false
    }
    const children = _.get(tenant, 'children', [])
    return instances?.length > 0 || children.length > 0 || !tenant || readOnly
  }, [instances?.length, readOnly, tenant])

  const deleteBlockingReasons = useMemo(() => {
    const children = _.get(tenant, 'children', [])
    const reasons = []
    if (readOnly) {
      reasons.push(LABEL_CANNOT_DELETE_TSG_ACCESS)
    }
    if (instances.length > 0) {
      const instanceNames = instances.map(instance => IAMAppName(instance.app_id, getAppName))
      const message = StringTemplate(LABEL_CANNOT_DELETE_TSG_PRODUCTS, [instanceNames.join(', ')])
      reasons.push(message)
    }
    if (children.length) {
      reasons.push(LABEL_CANNOT_DELETE_TSG_CHILDREN)
    }
    //Commented for the APPORTAL-10468
    /*if (claimInfo?.isClaimed) {
      reasons.push(LABEL_CANNOT_DELETE_TSG_CLAIMS)
    }*/
    return (
      <ul className='cm-mt-2'>
        {reasons.map((reason, index) => (
          <li key={index}><Body>{reason}</Body></li>
        ))}
      </ul>
    )
  }, [getAppName, instances, readOnly, tenant])

  return (
    <Modal
      size='sm'
      style={{
        zIndex: 1051,
        top: '50%',
        '--tw-translate-y': '-50%',
        minWidth: '400px',
        minHeight: '300px'
      }}
      isOpen={isOpen}
      onClose={closeModal}
    >
      <ModalHeader title={TITLE_DELETE_TENANT} />
      {loading ? (
        <ModalBody addClassName='tw-flex tw-justify-center tw-items-center'>
          <LoadingIcon size='xl'/>
        </ModalBody>
      ) : (
        <ModalBody addClassName='tw-break-words tw-overflow-auto'>
          {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
          {deleteDisabled ? (
            <>
              <Bold>{'This tenant cannot be deleted because of the following reasons:'}</Bold>
              {deleteBlockingReasons}
            </>
          ) : (
            <>
              <Body className='tw-break-words'>{StringTemplate(PARAGRAPH_DELETE_TENANT, [parentDisplayName || '', currentNode || 'Account'])}</Body>
              <Body className='tw-mt-2'>{PARAGRAPH_DELETE_TENANT_CANNOT_UNDONE}</Body>
              <Body className='tw-mt-4'><Bold>{PARAGRAPH_DELETE_CONTINUE_PROMPT}</Bold></Body>
            </>
          )}
        </ModalBody>
      )}
      <ModalFooter>
        <Button disabled={loading} onClick={closeModal}>{BUTTON_CANCEL}</Button>
        <Button
          disabled={loading || deleteDisabled}
          onClick={onDeleteTenant}
          appearance={'primary-destructive'}
        >{BUTTON_CONFIRM_YES_DELETE_TENANT}</Button>
      </ModalFooter>
    </Modal>
  )
}

DeleteTSGModal.propTypes = {}
export default DeleteTSGModal
